import { useEffect, useMemo } from "react";

import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import useModalContext from "common/providers/modal/useModalContext";
import FinanceOptionsExplained from "features/vehicleDetails/modals/FinanceOptionsExplained";
import type { ResolvedSidebarButton } from "resolvers/data/sidebarLinkResolver";
import type { ResolvedSidebar } from "resolvers/data/sidebarResolver";
import formatCurrency from "utils/formatCurrency";

import styles from "./PriceFinanceSummary.module.scss";

const cx = classNames.bind(styles);

export type PriceDiscount = {
    moneyOff: number;
    previousPrice: number;
};

export type PriceFinanceSummaryProps = {
    price: number;
    originalRetailPrice: number;
    savingsAgainstOriginalRetailPrice: number;
    financeType?: "PCP" | "HP";
    financeAmount?: number;
    fractionDigits?: number;
    financeText?: string;
    depositText?: string;
    priceText?: string;
    ctaLeftText?: ResolvedSidebarButton;
    ctaRightText?: string;
    handleCtaRightClick?: () => void;
    modals: { [key: string]: ResolvedSidebar };
    discount?: PriceDiscount;
    isCommercial: boolean;
    hasVat: boolean;
    discountThreshold: number;
};

const PriceFinanceSummary = ({
    financeType,
    financeAmount,
    price,
    originalRetailPrice,
    savingsAgainstOriginalRetailPrice,
    fractionDigits = 0,
    financeText,
    depositText,
    priceText,
    ctaLeftText,
    ctaRightText,
    handleCtaRightClick,
    modals,
    discount,
    isCommercial,
    hasVat,
}: PriceFinanceSummaryProps) => {
    const finalPrice = price ?? 0;
    const { useSidebar } = useModalContext();
    const { registerSidebars, openRegisteredSidebar } = useSidebar();

    const transformedModals = useMemo(() => {
        if (!modals) return {};

        return Object.keys(modals).reduce(
            (res, key) => {
                res[key] = {
                    title: modals[key].title,
                    content: {
                        component: FinanceOptionsExplained,
                        props: modals[key].content,
                    },
                };
                return res;
            },
            {} as { [key: string]: any }
        );
    }, [modals]);

    useEffect(() => {
        if (Object.keys(transformedModals).length) {
            registerSidebars(transformedModals);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transformedModals]);

    const { moneyOff, previousPrice } = discount ?? { moneyOff: 0, previousPrice: 0 };

    const isDiscounted = !!moneyOff && !!previousPrice;

    return (
        <>
            <div id="price-finance" className={cx("priceFinance", { isDiscounted })}>
                <input type="hidden" id="originalRetailPrice" value={originalRetailPrice} />
                <input
                    type="hidden"
                    id="savingsAgainstOriginalRetailPrice"
                    value={savingsAgainstOriginalRetailPrice}
                />
                {isDiscounted && (
                    <div className={cx("discountBadge")}>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: `${formatCurrency(discount?.moneyOff ?? 0, 0, true)} OFF`,
                            }}
                        />{" "}
                    </div>
                )}
                <div className={cx("depositFinance")}>
                    {!!financeType && !!financeAmount && (
                        <div className={cx("finance")}>
                            <strong
                                className={cx("styledPrice")}
                                dangerouslySetInnerHTML={{
                                    __html: formatCurrency(financeAmount, fractionDigits),
                                }}
                            />{" "}
                            <span className={cx("priceText")}>
                                {financeText} {financeType}
                            </span>
                        </div>
                    )}
                </div>
                {!!finalPrice && !!financeType && !!financeAmount && (
                    <div className={cx("orContainer")}>
                        <div className={cx("text")}>Or</div>
                        <span className={cx("orIcon")}></span>
                    </div>
                )}
                {!!finalPrice && (
                    <div className={cx("fullPrice", { isDiscounted })}>
                        <strong
                            className={cx("styledPrice")}
                            dangerouslySetInnerHTML={{
                                __html: formatCurrency(finalPrice, fractionDigits, true),
                            }}
                        />
                        {!isDiscounted && (
                            <span className={cx("priceText")}>
                                {isCommercial && hasVat ? "+VAT" : priceText}
                            </span>
                        )}
                        {isDiscounted && (
                            <span className={cx("priceText", { isDiscounted })}>
                                was{" "}
                                <span
                                    className={cx("previousPrice")}
                                    dangerouslySetInnerHTML={{
                                        __html: formatCurrency(
                                            discount?.previousPrice ?? 0,
                                            fractionDigits
                                        ),
                                    }}
                                />
                                <span className={cx("plusVat")}>{isCommercial && " +VAT"}</span>
                            </span>
                        )}
                    </div>
                )}
            </div>
            <div id="price-finance-buttons" className={cx("buttonRow")}>
                {!!ctaLeftText && (
                    <Button
                        id="vehicle-details-question-cta"
                        onClick={() => {
                            openRegisteredSidebar(ctaLeftText.params.sidebarId, {
                                openByDefault: ctaLeftText.params.anchor,
                            });
                        }}
                        type="button"
                        theme="textLink"
                        ariaLabel={ctaLeftText.label}
                    >
                        {ctaLeftText.label}
                    </Button>
                )}
                {!!ctaRightText && handleCtaRightClick && (
                    <Button
                        id="build-finance-deal-cta"
                        onClick={handleCtaRightClick}
                        type="button"
                        theme="textLink"
                        ariaLabel={ctaRightText}
                    >
                        {ctaRightText}
                    </Button>
                )}
            </div>
        </>
    );
};

export default PriceFinanceSummary;
